/**
 * Packshot gallery for the product page (snippets/packshot-gallery.liquid), ported from Spejder
 * Sport's PDP gallery (assets/spejder-pdp.js initGallery): thumbnails, looping arrows, a mobile
 * swipe row with progress dots, and a full-screen zoom view.
 */
import { StandardEvents } from '@shopify/events';

/** Swipe distance (px) that changes the image on the stacked (fading) track and in the zoom view. */
const SWIPE_THRESHOLD = 30;

/** A cut of at least this many px on the last visible thumb reads as a deliberate "more below" peek. */
const MIN_THUMB_PEEK = 16;

class PackshotGallery extends HTMLElement {
  #controller = new AbortController();
  #current = 0;
  /** @type {{ head: HTMLElement, tail: HTMLElement } | null} */
  #clones = null;
  #touching = false;
  /** @type {number | undefined} */
  #settleTimer;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    this.thumbs = [...this.querySelectorAll('[data-packshot-thumb]')];
    this.slidesWrap = /** @type {HTMLElement} */ (this.querySelector('[data-packshot-slides]'));
    this.slides = [...this.querySelectorAll('[data-packshot-slide]')];
    this.dots = [...this.querySelectorAll('[data-packshot-progress] [data-index]')];
    this.main = this.querySelector('[data-packshot-main]');
    this.zoomDialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-packshot-zoom-dialog]'));
    this.count = this.slides.length;

    const { signal } = this.#controller;

    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal,
    });

    if (!this.count) return;

    this.#observeMainHeight();

    this.thumbs.forEach((thumb) =>
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index)), { signal })
    );
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.index)), { signal }));
    this.querySelector('[data-packshot-prev]')?.addEventListener('click', () => this.goTo(this.#current - 1), {
      signal,
    });
    this.querySelector('[data-packshot-next]')?.addEventListener('click', () => this.goTo(this.#current + 1), {
      signal,
    });

    this.#bindTrack(signal);
    this.#bindZoom(signal);

    this.#syncClones();
    if (this.#clones) this.slidesWrap.scrollTo({ left: this.#trackLeft(0), behavior: 'auto' });
    this.#render(0);
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#settleTimer);
  }

  /**
   * Shows the slide at `index`; -1 and count wrap around (the gallery loops).
   * @param {number} index
   * @param {ScrollBehavior} [behavior]
   */
  goTo(index, behavior = 'smooth') {
    const target = this.#wrap(index);
    // Resting on a loop clone (a second tap within the settle window)? Re-seat on the real twin first,
    // so the move starts there instead of animating back across the whole track.
    if (this.#clones) this.#settleLoop();
    this.slidesWrap.scrollTo({ left: this.#trackLeft(this.#clones ? index : target), behavior });
    this.#render(target);
  }

  /**
   * Replaces the gallery with the re-rendered one when another variant or product is selected,
   * as Horizon's own media-gallery does.
   * @param {Event & { promise?: Promise<{ detail?: { html?: Document } }> }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        const next = detail?.html?.querySelector(`packshot-gallery[data-block-id="${this.dataset.blockId}"]`);
        if (next) this.replaceWith(next);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[packshot-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Keeps the thumbnail rail as tall as the main image. When the rail would clip less than
   * MIN_THUMB_PEEK px off a thumb, it ends cleanly after the previous thumb instead, since a
   * sliver reads as a broken box rather than a "more below" hint.
   */
  #observeMainHeight() {
    const rail = this.querySelector('[data-packshot-thumbs]');
    if (!this.main || !rail || !('ResizeObserver' in window)) return;

    this.#resizeObserver = new ResizeObserver(() => {
      let height = this.main.getBoundingClientRect().height;
      const first = /** @type {HTMLElement | null} */ (rail.firstElementChild);
      if (!height || !first) return;

      const gap = parseFloat(getComputedStyle(rail).rowGap) || 8;
      const row = first.offsetHeight + gap;
      for (let j = 1; j < rail.children.length; j++) {
        const cut = j * row + first.offsetHeight - height;
        if (cut >= MIN_THUMB_PEEK) break;
        if (cut > 0) {
          // +2 keeps the previous thumb's border clear of the clip edge (the rail's padding-bottom)
          height = j * row - gap + 2;
          break;
        }
      }
      this.style.setProperty('--packshot-gallery-height', `${height}px`);
    });
    this.#resizeObserver.observe(this.main);
  }

  /**
   * Mobile lays the slides out as a native scroll-snap row. A native row cannot wrap, and at either end
   * iOS hands an unconsumable swipe to the page, so the row gets a clone of the last slide before the
   * first and of the first after the last; once a swipe rests on a clone it is re-seated on the real
   * twin with an instant, identical-looking jump (#settleLoop). Desktop stacks the slides and fades.
   * @param {AbortSignal} signal
   */
  #bindTrack(signal) {
    let ticking = false;
    this.slidesWrap.addEventListener(
      'scroll',
      () => {
        this.#armSettle();
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const width = this.slidesWrap.clientWidth || 1;
          const slot = Math.round(this.slidesWrap.scrollLeft / width);
          const index = this.#wrap(this.#clones ? slot - 1 : slot);
          if (index !== this.#current) this.#render(index);
          ticking = false;
        });
      },
      { passive: true, signal }
    );

    // The scrolling row snaps natively; a resting finger holds the clone re-seat back, since a jump under
    // it would yank the slide. The stacked (fading) track on touch tablets is swiped by threshold instead.
    let touchStartX = /** @type {number | null} */ (null);
    this.slidesWrap.addEventListener(
      'touchstart',
      (event) => {
        this.#touching = true;
        touchStartX = event.touches[0].clientX;
      },
      { passive: true, signal }
    );
    const release = (/** @type {TouchEvent} */ event) => {
      this.#touching = false;
      const startX = touchStartX;
      touchStartX = null;
      if (this.#clones) {
        this.#armSettle();
        return;
      }
      if (startX === null || this.count < 2) return;
      const deltaX = event.changedTouches[0].clientX - startX;
      if (Math.abs(deltaX) >= SWIPE_THRESHOLD) this.goTo(this.#current + (deltaX < 0 ? 1 : -1));
    };
    this.slidesWrap.addEventListener('touchend', release, { passive: true, signal });
    this.slidesWrap.addEventListener('touchcancel', release, { passive: true, signal });

    window.addEventListener(
      'resize',
      () => {
        this.#syncClones();
        this.slidesWrap.scrollTo({ left: this.#trackLeft(this.#current), behavior: 'auto' });
      },
      { signal }
    );
  }

  /** @param {AbortSignal} signal */
  #bindZoom(signal) {
    const dialog = this.zoomDialog;
    if (!dialog) return;

    // The full-size image is created here rather than in the markup, so nothing loads until zoom opens
    this.zoomImage = document.createElement('img');
    this.zoomImage.className = 'packshot-gallery__zoom-image';
    this.zoomImage.decoding = 'async';
    dialog.prepend(this.zoomImage);

    this.querySelector('[data-packshot-zoom]')?.addEventListener(
      'click',
      () => {
        this.#showZoomed(this.#current);
        dialog.showModal();
      },
      { signal }
    );
    this.querySelector('[data-packshot-zoom-close]')?.addEventListener('click', () => dialog.close(), { signal });
    this.querySelector('[data-packshot-zoom-prev]')?.addEventListener('click', () => this.#stepZoom(-1), { signal });
    this.querySelector('[data-packshot-zoom-next]')?.addEventListener('click', () => this.#stepZoom(1), { signal });

    dialog.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowLeft') this.#stepZoom(-1);
        if (event.key === 'ArrowRight') this.#stepZoom(1);
      },
      { signal }
    );

    let touchStartX = /** @type {number | null} */ (null);
    dialog.addEventListener('touchstart', (event) => (touchStartX = event.touches[0].clientX), {
      passive: true,
      signal,
    });
    dialog.addEventListener(
      'touchend',
      (event) => {
        if (touchStartX === null) return;
        const deltaX = event.changedTouches[0].clientX - touchStartX;
        touchStartX = null;
        if (Math.abs(deltaX) >= SWIPE_THRESHOLD) this.#stepZoom(deltaX < 0 ? 1 : -1);
      },
      { passive: true, signal }
    );

    // Closing leaves the page gallery on the image last viewed in full screen
    dialog.addEventListener('close', () => this.goTo(this.#current, 'auto'), { signal });
  }

  /** @param {number} step */
  #stepZoom(step) {
    this.#showZoomed(this.#wrap(this.#current + step));
  }

  /** @param {number} index */
  #showZoomed(index) {
    const slide = this.slides[index];
    if (!slide || !this.zoomImage) return;
    this.#current = index;
    this.zoomImage.src = slide.dataset.zoomSrc ?? '';
    this.zoomImage.alt = slide.dataset.zoomAlt ?? '';
  }

  #trackScrolls() {
    return this.count > 1 && this.slides[1].offsetLeft !== this.slides[0].offsetLeft;
  }

  /** @param {HTMLElement} slide */
  #makeClone(slide) {
    const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
    clone.classList.remove('is-active');
    clone.removeAttribute('data-packshot-slide');
    clone.removeAttribute('data-index');
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('img').forEach((img) => img.removeAttribute('fetchpriority'));
    return clone;
  }

  #syncClones() {
    const scrolls = this.#trackScrolls();
    if (scrolls && !this.#clones) {
      this.#clones = {
        head: this.#makeClone(/** @type {HTMLElement} */ (this.slides[this.count - 1])),
        tail: this.#makeClone(/** @type {HTMLElement} */ (this.slides[0])),
      };
      this.slidesWrap.insertBefore(this.#clones.head, this.slides[0]);
      this.slidesWrap.appendChild(this.#clones.tail);
    } else if (!scrolls && this.#clones) {
      this.#clones.head.remove();
      this.#clones.tail.remove();
      this.#clones = null;
    }
  }

  /**
   * Scroll offset of a slide index; with clones the real slides sit one slot to the right and
   * -1 / count address the clones.
   * @param {number} index
   */
  #trackLeft(index) {
    return (this.#clones ? index + 1 : index) * this.slidesWrap.clientWidth;
  }

  /** @param {number} index */
  #wrap(index) {
    return ((index % this.count) + this.count) % this.count;
  }

  #armSettle() {
    clearTimeout(this.#settleTimer);
    this.#settleTimer = window.setTimeout(() => this.#settleLoop(), 100);
  }

  /** Re-seats a row resting on a loop clone onto the real twin. */
  #settleLoop() {
    if (!this.#clones || this.#touching) return;
    const width = this.slidesWrap.clientWidth || 1;
    const left = this.slidesWrap.scrollLeft;
    const slot = Math.round(left / width);
    if (Math.abs(left - slot * width) > 1) return; // still moving
    if (slot === 0) {
      this.slidesWrap.scrollTo({ left: this.count * width, behavior: 'auto' });
    } else if (slot === this.count + 1) {
      this.slidesWrap.scrollTo({ left: width, behavior: 'auto' });
    }
  }

  /** @param {number} index */
  #render(index) {
    this.#current = index;
    for (const list of [this.thumbs, this.slides, this.dots]) {
      list.forEach((element) =>
        element.classList.toggle('is-active', Number(/** @type {HTMLElement} */ (element).dataset.index) === index)
      );
    }
    this.thumbs.forEach((thumb) => thumb.setAttribute('aria-current', String(Number(thumb.dataset.index) === index)));

    const activeThumb = this.thumbs.find((thumb) => Number(thumb.dataset.index) === index);
    if (activeThumb) this.#revealThumb(activeThumb);
  }

  /**
   * Scrolls the thumbnail rail (a column on desktop, a row on tablets) just enough to show the active
   * thumb. Scrolls only the rail, never the page, unlike scrollIntoView.
   * @param {HTMLElement} thumb
   */
  #revealThumb(thumb) {
    const rail = thumb.parentElement;
    if (!rail) return;
    const railBox = rail.getBoundingClientRect();
    const box = thumb.getBoundingClientRect();
    const left = box.left < railBox.left ? box.left - railBox.left : Math.max(0, box.right - railBox.right);
    const top = box.top < railBox.top ? box.top - railBox.top : Math.max(0, box.bottom - railBox.bottom);
    if (left || top) rail.scrollBy({ left, top, behavior: 'smooth' });
  }
}

if (!customElements.get('packshot-gallery')) {
  customElements.define('packshot-gallery', PackshotGallery);
}
